// src/store/modules/leads.js
import {
    fetchLeads   as apiFetchLeads,
    updateLead   as apiUpdateLead,
    createLead   as apiCreateLead,
    fetchComments as apiFetchComments,
    postComment   as apiPostComment,
    fetchUsers as apiFetchUsers,
    generateEmail as apiGenerateEmail,
    fetchCallAnalytics as apiFetchCallAnalytics,
    fetchCalls as apiFetchCalls 
  } from '@/api/leads'
  
  export default {
    namespaced: true,
    state: () => ({
      all: [],
      comments: {},    // maps leadId -> array of comments
      userMap: {},
      users: [],
      callAnalytics: {},
      calls: [],     // holds fetched calls
      callsMeta: {}  // holds pagination meta for calls

    }),
    mutations: {
      SET_LEADS(state, leads) {
        state.all = leads
      },
      UPDATE_LEAD(state, updatedLead) {
        if (!updatedLead || typeof updatedLead !== 'object') return
        state.all = state.all.map(l =>
          l.id === updatedLead.id ? updatedLead : l
        )
      },
      ADD_LEAD(state, newLead) {
        state.all.unshift(newLead)
      },
      // comments mutations
      SET_COMMENTS(state, { leadId, comments }) {
        state.comments = { 
          ...state.comments,
          [leadId]: comments 
        }
      },
      ADD_COMMENT(state, { leadId, comment }) {
        if (!state.comments[leadId]) {
          // initialize if absent
          state.comments[leadId] = []
        }
        state.comments[leadId].push(comment)
      },
      SET_USER_MAP(state, map) {
        state.userMap = map
      },
      SET_USERS(state, users) {
        state.users = users
      },
      SET_CALL_ANALYTICS(state, analytics) {
        state.callAnalytics = analytics
      },
      SET_CALLS(state, { calls, meta }) {
        state.calls = calls
        state.callsMeta = meta
      }
      
    },
    actions: {
      async fetchCalls({ commit }, params = {}) {
        try {
          const res = await apiFetchCalls(params)
          // Check if res.data is an array
          commit('SET_CALLS', { calls: res.data, meta: res.meta })
          return res;
        } catch (e) {
          console.error('fetchCalls failed', e)
          throw e
        }
      },
      async fetchLeads({ commit }, params = {}) {
        try {
          const res = await apiFetchLeads(params)
          commit('SET_LEADS', res.data)
        } catch (e) {
          console.error('fetchLeads failed', e)
        }
      },
      async updateLead({ commit }, { id, updates }) {
        try {
          const res = await apiUpdateLead(id, updates)
          const leadObj = res.data ?? res
          commit('UPDATE_LEAD', leadObj)
          return leadObj
        } catch (e) {
          console.error('updateLead failed', e)
          throw e
        }
      },
      async createLead({ commit }, leadData) {
        try {
          const res = await apiCreateLead(leadData)
          const newLead = res.data ?? res
          commit('ADD_LEAD', newLead)
          return newLead
        } catch (e) {
          console.error('createLead failed', e)
          throw e
        }
      },
      // new actions for comments
      async fetchComments({ commit }, leadId) {
        try {
          const res = await apiFetchComments(leadId)
          const comments = res.data ?? res
          commit('SET_COMMENTS', { leadId, comments })
          return comments
        } catch (e) {
          console.error('fetchComments failed', e)
          throw e
        }
      },
      async addComment({ commit }, { leadId, text }) {
        try {
          const res = await apiPostComment(leadId, text)
          const comment = res.data ?? res
          commit('ADD_COMMENT', { leadId, comment })
          return comment
        } catch (e) {
          console.error('addComment failed', e)
          throw e
        }
      },
      async fetchUserMap({ commit }) {
        try {
          const res = await apiFetchUsers()
          const map = {}
          commit('SET_USERS', res)

          res.forEach(u => { map[u.id] = u.name });
          commit('SET_USER_MAP', map)

        } catch (e) {
          console.error('fetchUserMap failed', e)
        }
      },
      async generateEmail({ commit }, { lead, considerations }) {
        try {
          const res = await apiGenerateEmail({ lead, considerations });
      
          // Optionally update lead in the store if backend returns updated lead data
          if (res.updatedLead) {
            commit('UPDATE_LEAD', res.updatedLead);
          }
      
          // Return the email string
          return { body: res.body, subject: res.subject };
        } catch (e) {
          console.error('generateEmail failed', e);
          throw e;
        }
      },
      async fetchCallAnalytics({ commit }, range = 'week') {
        try {
          const res = await apiFetchCallAnalytics(range)
          commit('SET_CALL_ANALYTICS', res)
        } catch (e) {
          console.error('fetchCallAnalytics failed', e)
          throw e
        }
      }
    },
    getters: {
      filtered: state => state.all,
      commentsFor: state => leadId => state.comments[leadId] || [],
      usernameById: state => id => state.userMap[id] || 'Unknown',
      users: state => state.userMap,
      allUsers: state => state.users,
      usersFiltered: (state, getters, rootState, rootGetters) => {
        const me = rootGetters['auth/currentUser'] || rootState?.auth?.user
        const users = Array.isArray(state.users)
          ? state.users
          : Object.values(state.users || state.userMap || {})
      
        if (!users.length || !me) return {}
      
        const meId  = Number(me.id)
        const meOrg = String(me.organization ?? '')
      
        const isVisible = (u) =>
          Number(u.id) === meId || u.show_on_dashboard === 1
      
        const list = (meId === 1)
          ? users.filter(isVisible)
          : users.filter(u =>
              String(u.organization ?? '') === meOrg && isVisible(u)
            )
      
        // 🚫 strip out id 1 regardless
        const filtered = list.filter(u => Number(u.id) !== 1)
      
        return Object.fromEntries(filtered.map(u => [u.id, u.name ?? u.email ?? `User ${u.id}`]))
      },      
      callAnalytics: state => state.callAnalytics,
      calls: state => state.calls,
      callsMeta: state => state.callsMeta,
      
    }
  }
  